import type { ChangeEvent, ReactNode } from 'react';
import { CheckIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type CheckboxRowProps = {
  /** 是否勾選 */
  checked: boolean;
  /** 勾選狀態變更 */
  onCheckedChange: (checked: boolean) => void;
  /** 列內容（不含勾選框） */
  children: ReactNode;
  /** 外框 className */
  className?: string;
  /** 勾選框 className */
  checkboxClassName?: string;
  /** 勾選框位置 */
  checkboxPosition?: 'start' | 'end';
  /** 是否停用 */
  disabled?: boolean;
};

/**
 * 功能：繪製純視覺勾選框（pointer-events-none，點擊由外層 label 承接）
 * @param {boolean} checked 是否勾選
 * @param {string} [className] 額外樣式
 * @returns {JSX.Element} 視覺勾選框
 */
function CheckboxVisual({ checked, className }: { checked: boolean; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none relative z-10 flex size-[18px] shrink-0 items-center justify-center rounded-md border-2 shadow-sm transition-colors',
        'border-muted-foreground/50 bg-zinc-900/90',
        checked && 'border-primary bg-primary text-primary-foreground shadow-none',
        className,
      )}
    >
      {checked ? <CheckIcon className="pointer-events-none size-3.5" /> : null}
    </span>
  );
}

/**
 * 功能：一體式勾選列——label + 原生 checkbox，整列（含勾選圖示）皆可切換
 * @param {CheckboxRowProps} props 勾選狀態、內容與樣式
 * @returns {JSX.Element} 可點擊的勾選列
 */
export function CheckboxRow({
  checked,
  onCheckedChange,
  children,
  className,
  checkboxClassName,
  checkboxPosition = 'start',
  disabled = false,
}: CheckboxRowProps) {
  /**
   * 功能：原生 checkbox 變更時回傳新狀態
   * @param {ChangeEvent<HTMLInputElement>} e change 事件
   * @returns {void}
   */
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onCheckedChange(e.target.checked);
  };

  const box = <CheckboxVisual checked={checked} className={checkboxClassName} />;

  return (
    <label
      className={cn(
        'relative flex w-full items-center gap-3 text-left cursor-pointer select-none transition-all',
        'has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/40',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
      />
      {checkboxPosition === 'start' ? box : null}
      <div className="relative z-0 flex-1 min-w-0">{children}</div>
      {checkboxPosition === 'end' ? box : null}
    </label>
  );
}
